import { useMemo } from 'react';
import styled from 'styled-components';

const MIN_TEMP = 0;
const MAX_TEMP = 120;
const ARC_START = 135;
const ARC_SWEEP = 270;
const RADIUS = 100;
const STROKE_WIDTH = 12;
const CENTER = 120;

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
};

const ThermalGauge = ({ currentTemp, setTemp, isF }) => {
  const numericCurrent = typeof currentTemp === 'number' ? currentTemp : null;
  const numericSet = typeof setTemp === 'number' ? setTemp : null;
  const delta = numericCurrent != null && numericSet != null
    ? (numericCurrent - numericSet).toFixed(1)
    : null;

  const { needleAngle, arcPath, filledPath } = useMemo(() => {
    const arcEnd = ARC_START + ARC_SWEEP;
    const arcP = describeArc(CENTER, CENTER, RADIUS, ARC_START, arcEnd);

    if (numericCurrent == null) {
      return { needleAngle: ARC_START, arcPath: arcP, filledPath: null };
    }

    const clamped = Math.max(MIN_TEMP, Math.min(MAX_TEMP, numericCurrent));
    const ratio = (clamped - MIN_TEMP) / (MAX_TEMP - MIN_TEMP);
    const angle = ARC_START + ratio * ARC_SWEEP;
    const filledP = describeArc(CENTER, CENTER, RADIUS, ARC_START, angle);

    return { needleAngle: angle, arcPath: arcP, filledPath: filledP };
  }, [numericCurrent]);

  const indicatorPos = polarToCartesian(CENTER, CENTER, RADIUS, needleAngle);
  const unit = isF ? '°F' : '°C';

  return (
    <Wrapper>
      <InfoRow>
        <InfoItem>
          <InfoLabel>CURRENT</InfoLabel>
          <InfoValue $color="#00D492">
            {numericCurrent != null ? `${numericCurrent}${unit}` : `--${unit}`}
          </InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>SET POINT</InfoLabel>
          <InfoValue $color="#EE4B56">
            {numericSet != null ? `${numericSet}${unit}` : `--${unit}`}
          </InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>DELTA</InfoLabel>
          <InfoValue $color="#3B82F6">
            {delta != null ? `${delta > 0 ? '+' : ''}${delta}${unit}` : `--${unit}`}
          </InfoValue>
        </InfoItem>
      </InfoRow>

      <GaugeSvg viewBox="0 0 240 240">

        <path
          d={arcPath}
          fill="none"
          stroke="#D9D9D9"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
        />


        {filledPath && (
          <path
            d={filledPath}
            fill="none"
            stroke="#3B82F6"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />
        )}


        <circle cx={indicatorPos.x} cy={indicatorPos.y} r={8} fill="#FF0000" />
        <circle cx={indicatorPos.x} cy={indicatorPos.y} r={4} fill="#ffffff" />


        <text x={CENTER} y={CENTER - 8} textAnchor="middle" fill="#ffffff" fontSize="36" fontWeight="700" fontFamily="monospace">
          {numericCurrent != null ? numericCurrent.toFixed(1) : '--.-'}°
        </text>
        <text x={CENTER} y={CENTER + 28} textAnchor="middle" fill="#90A1B9" fontSize="10" fontWeight="600" letterSpacing="1.5">
          CURRENT TEMP
        </text>
      </GaugeSvg>
    </Wrapper>
  );
};

export default ThermalGauge;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  background: rgba(19, 28, 48, 0.8);
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  border-radius: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--switch-text-faint);
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $color }) => $color || '#ffffff'};
`;

const GaugeSvg = styled.svg`
  width: 100%;
  max-width: 320px;
  height: auto;
`;
